import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import useSeo, { SITE_NAME, SITE_ORIGIN } from "@/hooks/useSeo";
import { rumors } from "@/mocks/rumors";
import { categories } from "@/mocks/categories";

type SortKey = "latest" | "hot";

export default function Rumors() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") || "all";
  const queryParam = searchParams.get("q") || "";
  const [input, setInput] = useState(queryParam);
  const [sort, setSort] = useState<SortKey>("latest");

  useEffect(() => {
    setInput(queryParam);
  }, [queryParam]);

  const filtered = useMemo(() => {
    const keyword = queryParam.trim().toLowerCase();
    const list = rumors.filter((item) => {
      const matchCat = activeCat === "all" || item.categorySlug === activeCat;
      if (!matchCat) return false;
      if (!keyword) return true;
      return (
        item.title.toLowerCase().includes(keyword) ||
        item.excerpt.toLowerCase().includes(keyword) ||
        item.tags.some((tag) => tag.toLowerCase().includes(keyword)) ||
        item.categoryName.toLowerCase().includes(keyword)
      );
    });
    return [...list].sort((a, b) =>
      sort === "hot" ? b.heat - a.heat : b.publishedAt.localeCompare(a.publishedAt)
    );
  }, [activeCat, queryParam, sort]);

  const activeCatName =
    activeCat === "all" ? "全部栏目" : categories.find((c) => c.slug === activeCat)?.name || "全部栏目";

  const updateParams = (next: { cat?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.cat !== undefined) {
      if (next.cat === "all") params.delete("cat");
      else params.set("cat", next.cat);
    }
    if (next.q !== undefined) {
      if (!next.q) params.delete("q");
      else params.set("q", next.q);
    }
    setSearchParams(params, { replace: true });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateParams({ q: input.trim() });
  };

  useSeo({
    title: `${activeCatName}爆料汇总 - 热点内幕一手信息持续更新`,
    description: `浏览热点内幕${activeCatName}的独家爆料合集，汇总知情人透露的小道消息与秘密流出内容，按最新与热度排序，一手信息持续更新。`,
    keywords: "热点内幕,独家爆料,知情人透露,一手信息,爆料汇总",
    path: activeCat === "all" ? "/rumors" : `/rumors?cat=${activeCat}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${activeCatName}爆料汇总`,
      url: `${SITE_ORIGIN}/rumors`,
      isPartOf: { "@type": "WebSite", name: SITE_NAME, url: `${SITE_ORIGIN}/` },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: filtered.length,
        itemListElement: filtered.slice(0, 10).map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_ORIGIN}/article/${item.id}`,
          name: item.title,
        })),
      },
    },
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="container-editorial py-8 md:py-10">
            <Breadcrumbs items={[{ label: "爆料中心" }]} />
            <h1 className="mt-4 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
              爆料中心
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              汇总站内全部独家爆料与知情人透露内容，支持按栏目筛选与关键词搜索。想看的内部消息，从这里开始翻。
            </p>
          </div>
        </section>

        <section className="container-editorial py-8 md:py-10">
          <div className="rounded-lg border border-background-200/80 bg-background-50 p-4 md:p-5">
            <form onSubmit={submitSearch} className="flex flex-col gap-3 sm:flex-row">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5">
                <i className="ri-search-line text-base text-foreground-500" />
                <input
                  type="text"
                  name="q"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="搜索标题、标签或关键词，如「撤档」「对赌」"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
                {input && (
                  <button
                    type="button"
                    onClick={() => {
                      setInput("");
                      updateParams({ q: "" });
                    }}
                    aria-label="清空搜索"
                    className="text-foreground-400 transition-colors hover:text-foreground-700"
                  >
                    <i className="ri-close-circle-fill text-base" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-search-line text-base" />
                搜索
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => updateParams({ cat: "all" })}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  activeCat === "all"
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => updateParams({ cat: cat.slug })}
                  className={`inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    activeCat === cat.slug
                      ? "bg-primary-600 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`} />
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-foreground-950">{filtered.length}</span> 条爆料
              {queryParam && (
                <>
                  ，关键词「<span className="font-semibold text-primary-700">{queryParam}</span>」
                </>
              )}
            </p>
            <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              {[
                { key: "latest" as SortKey, label: "最新", icon: "ri-time-line" },
                { key: "hot" as SortKey, label: "最热", icon: "ri-fire-line" },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setSort(item.key)}
                  className={`inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    sort === item.key
                      ? "bg-background-50 text-primary-700"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <i className={`${item.icon} text-sm`} />
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item, index) => (
                <ArticleCard key={item.id} rumor={item} variant="grid" index={index} />
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-950">没有匹配的爆料</p>
              <p className="mt-1.5 text-sm text-foreground-600">换个关键词，或者直接看看全部栏目。</p>
              <button
                type="button"
                onClick={() => {
                  setInput("");
                  setSearchParams({}, { replace: true });
                }}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-refresh-line text-base" />
                重置筛选
              </button>
            </div>
          )}

          <div className="mt-10 rounded-lg border border-background-200/80 bg-background-100/60 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/rumors?cat=${cat.slug}`}
                  className="rounded-full bg-background-50 px-3.5 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                  {cat.name}爆料
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}